/**
 * WorkspaceView Component
 * Redesigned: Search-focused with floating chat and results side panel
 */

import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useParams, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  Panel,
  PanelGroup,
  PanelResizeHandle,
} from 'react-resizable-panels'
import {
  ArrowLeftIcon,
  PaperAirplaneIcon,
  SparklesIcon,
  BookmarkIcon,
  XMarkIcon,
  ArrowPathIcon,
  BookOpenIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  RectangleStackIcon,
  ChatBubbleLeftRightIcon,
  XCircleIcon,
  TrashIcon,
  LinkIcon,
  AcademicCapIcon,
  CpuChipIcon,
} from '@heroicons/react/24/outline'
import { CheckIcon as CheckIconSolid } from '@heroicons/react/24/solid'
import { workspacesAPI, type Workspace, type WorkspaceMessage, type PaperResult } from '@/api/workspaces'
import { citationLibraryAPI } from '@/api/citationLibrary'
import { aiProvidersAPI, type UserAIPreferences } from '@/api/aiProviders'
import { tokenUsageAPI, type TokenUsageStats } from '@/api/tokenUsage'
import { useDocumentStore } from '@stores/documentStore'
import { logError } from '@utils/logger'
import toast from 'react-hot-toast'
import clsx from 'clsx'
import { generateBibtexEntry, generateBibtexKey } from '@/utils/generateBibtex'
import PaperDetailModal from '@components/research/PaperDetailModal'
import { ConfirmDialog } from '@components/common/ConfirmDialog'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import type { CitationPaper } from '@/api/citations'

type ResultViewMode = 'explain' | 'synthesis' | 'compare' | null

export const WorkspaceView: React.FC = () => {
  const { workspaceId } = useParams<{ workspaceId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { currentDocument } = useDocumentStore()
  const [workspace, setWorkspace] = useState<Workspace | null>(null)
  const [results, setResults] = useState<PaperResult[]>([])
  const [evidence, setEvidence] = useState<Set<string>>(new Set())
  const [evidencePapers, setEvidencePapers] = useState<PaperResult[]>([]) // Full evidence paper data
  const [selectedPapers, setSelectedPapers] = useState<Set<string>>(new Set())
  const [expandedPapers, setExpandedPapers] = useState<Set<string>>(new Set())
  const [isLoading, setIsLoading] = useState(true)
  const [isRetrieving, setIsRetrieving] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [showSearchConfirmation, setShowSearchConfirmation] = useState(false)
  const [pendingSearchQuery, setPendingSearchQuery] = useState('')
  const [evidenceExpanded, setEvidenceExpanded] = useState(true) // Expanded by default
  const [showResultsPanel, setShowResultsPanel] = useState(false)
  const [resultViewMode, setResultViewMode] = useState<ResultViewMode>(null)
  const [resultContent, setResultContent] = useState<string>('')
  const [resultCitations, setResultCitations] = useState<string[]>([])
  const [selectedPaper, setSelectedPaper] = useState<PaperResult | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [showChat, setShowChat] = useState(false)
  const [chatMessages, setChatMessages] = useState<WorkspaceMessage[]>([])
  const [chatInput, setChatInput] = useState('')
  const [isSendingChat, setIsSendingChat] = useState(false)
  const [aiPreferences, setAiPreferences] = useState<UserAIPreferences | null>(null)
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null)
  const [selectedModel, setSelectedModel] = useState<string | null>(null)
  const [showModelSelector, setShowModelSelector] = useState(false)
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; right: number } | null>(null)
  const modelSelectorButtonRef = useRef<HTMLButtonElement>(null)

  // Update dropdown position when it opens
  useEffect(() => {
    if (showModelSelector && modelSelectorButtonRef.current) {
      const updatePosition = () => {
        if (modelSelectorButtonRef.current) {
          const rect = modelSelectorButtonRef.current.getBoundingClientRect()
          setDropdownPosition({
            top: rect.bottom + window.scrollY + 8,
            right: window.innerWidth - rect.right
          })
        }
      }
      updatePosition()
      window.addEventListener('resize', updatePosition)
      window.addEventListener('scroll', updatePosition, true)
      return () => {
        window.removeEventListener('resize', updatePosition)
        window.removeEventListener('scroll', updatePosition, true)
      }
    } else {
      setDropdownPosition(null)
    }
  }, [showModelSelector])
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [syntheses, setSyntheses] = useState<any[]>([])
  const [evidenceTab, setEvidenceTab] = useState<'evidence' | 'syntheses'>('evidence')
  const resultsEndRef = useRef<HTMLDivElement>(null)
  const chatEndRef = useRef<HTMLDivElement>(null)
  const [showEvidenceDrawer, setShowEvidenceDrawer] = useState(false)

  const [userTokenStats, setUserTokenStats] = useState<TokenUsageStats | null>(null)

  useEffect(() => {
    if (workspaceId) {
      loadWorkspace()
      loadAIPreferences()
      loadUserTokenStats()
    }
  }, [workspaceId])

  useEffect(() => {
    if (showChat) {
      scrollChatToBottom()
    }
  }, [chatMessages, showChat])

  const scrollChatToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const loadAIPreferences = async () => {
    try {
      const prefs = await aiProvidersAPI.getPreferences()
      setAiPreferences(prefs)
      setSelectedProvider(prefs.provider || null)
      setSelectedModel(prefs.model || null)
    } catch (error) {
      logError('Error loading AI preferences:', error)
    }
  }

  const loadUserTokenStats = async () => {
    try {
      const stats = await tokenUsageAPI.getUserStats()
      setUserTokenStats(stats)
    } catch (error) {
      logError('Error loading user token stats:', error)
    }
  }

  const loadWorkspace = async () => {
    if (!workspaceId) return

    setIsLoading(true)
    try {
      const data = await workspacesAPI.getWorkspace(workspaceId)
      setWorkspace(data)
      setSearchQuery(data.query || '')

      const workspaceEvidence = await workspacesAPI.getWorkspaceEvidence(workspaceId)

      // Load previous syntheses
      let loadedSyntheses: any[] = []
      try {
        loadedSyntheses = await workspacesAPI.getSyntheses(workspaceId)
        setSyntheses(loadedSyntheses)
      } catch (error: any) {
        if (error?.response?.status !== 404) {
          logError('Error loading syntheses:', error)
        }
      }

      // Load chat history
      let messages: WorkspaceMessage[] = []
      try {
        messages = await workspacesAPI.getMessages(workspaceId)
        setChatMessages(messages)
      } catch (error: any) {
        if (error?.response?.status !== 404) {
          logError('Error loading chat messages:', error)
        }
      }

      // Load evidence papers independently of search results
      if (workspaceEvidence.length > 0) {
        const evidenceSet = new Set(workspaceEvidence.map((e: any) => e.paper_ref))
        setEvidence(evidenceSet)

        // Store full evidence paper data
        const evidencePaperData: PaperResult[] = workspaceEvidence
          .map((e: any) => {
            if (e.paper) {
              return {
                ...e.paper,
                paper_ref: e.paper_ref,
                relevance_score: undefined, // Evidence papers don't have relevance scores
              } as PaperResult
            }
            return null
          })
          .filter((p: PaperResult | null): p is PaperResult => p !== null)

        setEvidencePapers(evidencePaperData)
        setEvidenceExpanded(true) // Auto-expand if there's evidence
      }
    } catch (error) {
      logError('Error loading workspace:', error)
      toast.error('Failed to load workspace')
      navigate('/workspaces')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSearchConfirmation = () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a search query')
      return
    }

    setPendingSearchQuery(searchQuery.trim())
    setShowSearchConfirmation(true)
  }

  const confirmSearch = async () => {
    if (!workspaceId || !pendingSearchQuery) return

    setIsRetrieving(true)
    setShowSearchConfirmation(false)

    try {
      // Update workspace with query and set status to retrieving
      await workspacesAPI.updateWorkspace(workspaceId, undefined, 'retrieving', pendingSearchQuery)

      // Reload workspace to get updated query
      await loadWorkspace()

      // Trigger retrieval (will use the updated query from workspace)
      const papers = await workspacesAPI.retrievePapers(workspaceId, { max_results: 50 })
      setResults(papers)

      await workspacesAPI.updateWorkspace(workspaceId, undefined, 'ready')
      toast.success(`Found ${papers.length} papers`)
    } catch (error: any) {
      logError('Error retrieving papers:', error)
      const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to retrieve papers'
      if (errorMessage.includes('cannot be empty') || errorMessage.includes('empty')) {
        toast.error('Please enter a research question before searching')
      } else {
        toast.error(errorMessage)
      }
    } finally {
      setIsRetrieving(false)
      setPendingSearchQuery('')
    }
  }

  const handleExplainPaper = async (paper: PaperResult) => {
    if (!workspaceId) return

    setIsGenerating(true)
    setResultViewMode('explain')
    setShowResultsPanel(true)
    setResultContent('Generating explanation...')

    try {
      const response = await workspacesAPI.generateChatResponse(
        workspaceId,
        `Can you explain this paper: ${paper.title}?`,
        true,
        selectedProvider || undefined,
        selectedModel || undefined
      )

      setResultContent(response.content)
      setResultCitations(response.citations || [])
    } catch (error: any) {
      logError('Error explaining paper:', error)
      const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to generate explanation'
      toast.error(errorMessage)
      setResultContent(`**Error**\n\n${errorMessage}\n\nPlease try again.`)
    } finally {
      setIsGenerating(false)
    }
  }

  const handleExplainSelected = async () => {
    if (selectedPapers.size === 0) {
      toast.error('Please select at least one paper')
      return
    }

    if (!workspaceId) return

    setIsGenerating(true)
    setResultViewMode('explain')
    setShowResultsPanel(true)
    setResultContent('Generating explanation...')

    try {
      const selectedTitles = Array.from(selectedPapers)
        .map(ref => results.find(p => p.paper_ref === ref)?.title)
        .filter(Boolean)
        .join(', ')

      const response = await workspacesAPI.generateChatResponse(
        workspaceId,
        `Can you explain these papers: ${selectedTitles}?`,
        true,
        selectedProvider || undefined,
        selectedModel || undefined
      )

      setResultContent(response.content)
      setResultCitations(response.citations || [])
    } catch (error: any) {
      logError('Error explaining papers:', error)
      const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to generate explanation'
      toast.error(errorMessage)
      setResultContent(`**Error**\n\n${errorMessage}\n\nPlease try again.`)
    } finally {
      setIsGenerating(false)
    }
  }

  const handleCompareSelected = async () => {
    if (selectedPapers.size < 2) {
      toast.error('Please select at least 2 papers to compare')
      return
    }

    if (!workspaceId) return

    setIsGenerating(true)
    setResultViewMode('compare')
    setShowResultsPanel(true)
    setResultContent('Generating comparison...')

    try {
      const selectedTitles = Array.from(selectedPapers)
        .map(ref => results.find(p => p.paper_ref === ref)?.title)
        .filter(Boolean)
        .join(', ')

      const response = await workspacesAPI.generateChatResponse(
        workspaceId,
        `Compare these papers: ${selectedTitles}`,
        true,
        selectedProvider || undefined,
        selectedModel || undefined
      )

      setResultContent(response.content)
      setResultCitations(response.citations || [])
    } catch (error: any) {
      logError('Error comparing papers:', error)
      const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to generate comparison'
      toast.error(errorMessage)
      setResultContent(`**Error**\n\n${errorMessage}\n\nPlease try again.`)
    } finally {
      setIsGenerating(false)
    }
  }

  const handleSynthesizeSelected = async () => {
    if (evidence.size === 0) {
      toast.error('Please save some papers as evidence first. Click "Save" on papers to add them to evidence.')
      return
    }

    if (!workspaceId) return

    setIsGenerating(true)
    setResultViewMode('synthesis')
    setShowResultsPanel(true)
    setResultContent('Generating synthesis...')

    try {
      const synthesis = await workspacesAPI.generateSynthesis(
        workspaceId,
        'answer_question',
        true,
        selectedProvider || undefined,
        selectedModel || undefined
      )

      setResultContent(synthesis.output)
      setResultCitations(synthesis.citations || [])

      // Reload syntheses to include the new one
      try {
        const updatedSyntheses = await workspacesAPI.getSyntheses(workspaceId)
        setSyntheses(updatedSyntheses)
      } catch (error) {
        logError('Error reloading syntheses:', error)
      }

      toast.success('Synthesis generated')
    } catch (error: any) {
      logError('Error generating synthesis:', error)
      const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to generate synthesis'
      if (errorMessage.includes('No evidence papers') || errorMessage.includes('evidence')) {
        toast.error('Please save some papers as evidence first. Click "Save" on papers to add them to evidence.')
      } else {
        toast.error(errorMessage)
      }
      setResultContent(`**Error**\n\n${errorMessage}\n\nPlease try again or save more papers as evidence.`)
    } finally {
      setIsGenerating(false)
    }
  }

  const handleSaveEvidence = async (paperRef: string, paper: PaperResult) => {
    if (!workspaceId) return

    try {
      await workspacesAPI.addEvidence(workspaceId, { paper_ref: paperRef })
      setEvidence(prev => new Set([...prev, paperRef]))

      // Add to evidence papers if not already there
      setEvidencePapers(prev => {
        if (prev.some(p => p.paper_ref === paperRef)) {
          return prev
        }
        return [...prev, paper]
      })

      if (currentDocument?.id) {
        const bibtexKey = generateBibtexKey(paper as any)
        const bibtexEntry = generateBibtexEntry(paper as any, bibtexKey)

        await citationLibraryAPI.saveCitation(currentDocument.id, {
          title: paper.title,
          authors: paper.authors,
          year: paper.year,
          doi: paper.doi,
          arxiv_id: paper.arxiv_id,
          abstract: paper.abstract,
          venue: paper.venue,
          url: paper.url,
          bibtex_key: bibtexKey,
          bibtex_entry: bibtexEntry,
        })
      }

      toast.success('Saved to evidence')
    } catch (error) {
      logError('Error saving evidence:', error)
      toast.error('Failed to save evidence')
    }
  }

  const handleRemoveEvidence = async (paperRef: string) => {
    if (!workspaceId) return

    try {
      await workspacesAPI.removeEvidence(workspaceId, paperRef)
      setEvidence(prev => {
        const next = new Set(prev)
        next.delete(paperRef)
        return next
      })
      // Remove from evidence papers
      setEvidencePapers(prev => prev.filter(p => p.paper_ref !== paperRef))
      toast.success('Removed from evidence')
    } catch (error) {
      logError('Error removing evidence:', error)
      toast.error('Failed to remove evidence')
    }
  }

  const handlePaperClick = (paper: PaperResult) => {
    setExpandedPapers(prev => {
      const next = new Set(prev)
      if (next.has(paper.paper_ref)) {
        next.delete(paper.paper_ref)
      } else {
        next.add(paper.paper_ref)
      }
      return next
    })
  }

  const handlePaperDetail = (paper: PaperResult) => {
    setSelectedPaper(paper)
    setIsModalOpen(true)
  }

  const handleToggleSelect = (paperRef: string) => {
    setSelectedPapers(prev => {
      const next = new Set(prev)
      if (next.has(paperRef)) {
        next.delete(paperRef)
      } else {
        next.add(paperRef)
      }
      return next
    })
  }

  const handleSelectAll = () => {
    if (selectedPapers.size === results.length) {
      setSelectedPapers(new Set())
    } else {
      setSelectedPapers(new Set(results.map(p => p.paper_ref)))
    }
  }

  const handleSendChatMessage = async () => {
    if (!chatInput.trim() || isSendingChat || !workspaceId) return

    const userMessage = chatInput.trim()
    setChatInput('')
    setIsSendingChat(true)

    const tempUserMessage: WorkspaceMessage = {
      message_id: `temp-${Date.now()}`,
      workspace_id: workspaceId,
      role: 'user',
      content: userMessage,
    }
    setChatMessages(prev => [...prev, tempUserMessage])

    try {
      await workspacesAPI.addMessage(workspaceId, {
        role: 'user',
        content: userMessage,
      })

      const response = await workspacesAPI.generateChatResponse(
        workspaceId,
        userMessage,
        true,
        selectedProvider || undefined,
        selectedModel || undefined
      )
      setChatMessages(prev => [...prev, response])
    } catch (error) {
      logError('Error sending message:', error)
      toast.error('Failed to send message')
      setChatMessages(prev => prev.filter(m => m.message_id !== tempUserMessage.message_id))
    } finally {
      setIsSendingChat(false)
    }
  }

  const handleDeleteWorkspace = async () => {
    if (!workspaceId) return

    try {
      await workspacesAPI.deleteWorkspace(workspaceId)
      toast.success('Workspace deleted')
      navigate('/workspaces')
    } catch (error) {
      logError('Error deleting workspace:', error)
      toast.error('Failed to delete workspace')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full bg-slate-50 dark:bg-slate-900">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
          <p className="mt-4 text-sm text-slate-600 dark:text-slate-400">Loading workspace...</p>
        </div>
      </div>
    )
  }

  if (!workspace) {
    return (
      <div className="flex items-center justify-center h-full bg-slate-50 dark:bg-slate-900">
        <div className="text-center">
          <p className="text-sm text-slate-600 dark:text-slate-400">Workspace not found</p>
          <button
            onClick={() => navigate('/workspaces')}
            className="mt-4 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg"
          >
            Back to Workspaces
          </button>
        </div>
      </div>
    )
  }

  // Evidence list: combine papers from current results AND saved evidence papers
  const evidenceList = [
    ...results.filter(p => evidence.has(p.paper_ref)),
    ...evidencePapers.filter(p => !results.some(r => r.paper_ref === p.paper_ref)) // Add evidence papers not in current results
  ]
  const selectedPapersList = results.filter(p => selectedPapers.has(p.paper_ref))
  const isReturningWorkspaceView = results.length === 0 && (evidenceList.length > 0 || syntheses.length > 0)



  return (
    <div className="h-full flex flex-col bg-slate-50 dark:bg-slate-900 relative">
      {/* AI Model Selector - Top Bar */}
      {aiPreferences && (
        <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-purple-50/50 to-indigo-50/50 dark:from-purple-900/10 dark:to-indigo-900/10">
          <div className="flex items-center gap-2">
            <CpuChipIcon className="w-4 h-4 text-purple-600 dark:text-purple-400" />
            <span className="text-xs font-medium text-slate-700 dark:text-slate-300">AI Model:</span>
          </div>
          <div className="relative">
            <button
              ref={modelSelectorButtonRef}
              onClick={() => setShowModelSelector(!showModelSelector)}
              className={clsx(
                "flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200",
                "bg-white dark:bg-slate-800 border border-purple-200 dark:border-purple-800",
                "text-purple-700 dark:text-purple-300",
                "hover:bg-purple-50 dark:hover:bg-purple-900/20",
                "hover:border-purple-300 dark:hover:border-purple-700",
                "hover:shadow-sm active:scale-95",
                showModelSelector && "ring-2 ring-purple-500 dark:ring-purple-400"
              )}
              title="Select AI Model"
            >
              <span className="truncate max-w-[200px]">
                {selectedModel || selectedProvider || 'Default'}
              </span>
              {showModelSelector ? (
                <ChevronUpIcon className="w-3 h-3 flex-shrink-0" />
              ) : (
                <ChevronDownIcon className="w-3 h-3 flex-shrink-0" />
              )}
            </button>

            {showModelSelector && createPortal(
              <>
                {/* Backdrop */}
                <div
                  className="fixed inset-0 z-[9998] bg-black/20 backdrop-blur-sm"
                  onClick={() => setShowModelSelector(false)}
                />

                {/* Dropdown - Portal to body, positioned relative to button */}
                <div
                  className={clsx(
                    "fixed",
                    "bottom-0 left-0 right-0 sm:bottom-auto sm:left-auto",
                    "w-full sm:w-80",
                    "max-h-[70vh] sm:max-h-[500px]",
                    "bg-white dark:bg-slate-800",
                    "rounded-t-2xl sm:rounded-xl",
                    "shadow-2xl border-t sm:border border-slate-200 dark:border-slate-700",
                    "z-[9999] overflow-hidden",
                    "animate-in slide-in-from-bottom-4 sm:slide-in-from-top-2 fade-in duration-200"
                  )}
                  style={{
                    // Position relative to button on desktop
                    ...(typeof window !== 'undefined' && window.innerWidth >= 640 && dropdownPosition && {
                      top: `${dropdownPosition.top}px`,
                      right: `${dropdownPosition.right}px`,
                      left: 'auto',
                      bottom: 'auto',
                    })
                  }}>
                  {/* Header */}
                  <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20">
                    <div className="flex items-center gap-2">
                      <CpuChipIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                      <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                        AI Model Settings
                      </h3>
                    </div>
                    <button
                      onClick={() => setShowModelSelector(false)}
                      className="p-1.5 rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-white dark:hover:bg-slate-700 transition-colors"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Content - Scrollable */}
                  <div className="overflow-y-auto max-h-[calc(70vh-80px)] sm:max-h-[calc(80vh-80px)] p-4 space-y-4">
                    {/* Provider Selection */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2 uppercase tracking-wide">
                        Provider
                      </label>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={async () => {
                            setSelectedProvider(null)
                            setSelectedModel(null)
                            setShowModelSelector(false)
                            try {
                              await aiProvidersAPI.updatePreferences({ provider: null, model: null })
                              queryClient.invalidateQueries({ queryKey: ["aiPreferences"] })
                              toast.success('Reset to default provider')
                            } catch (error) {
                              logError("Error updating AI provider:", error)
                              toast.error('Failed to update provider')
                            }
                          }}
                          className={clsx(
                            "px-3 py-2 text-xs font-medium rounded-lg transition-all",
                            !selectedProvider
                              ? "bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md"
                              : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
                          )}
                        >
                          Default
                        </button>
                        {aiPreferences.available_providers.map((provider) => (
                          <button
                            key={provider}
                            onClick={async () => {
                              setSelectedProvider(provider)
                              setSelectedModel(null)
                              try {
                                await aiProvidersAPI.updatePreferences({ provider, model: null })
                                queryClient.invalidateQueries({ queryKey: ["aiPreferences"] })
                                toast.success(`Switched to ${provider}`)
                              } catch (error) {
                                logError("Error updating AI provider:", error)
                                toast.error('Failed to update provider')
                              }
                            }}
                            className={clsx(
                              "px-3 py-2 text-xs font-medium rounded-lg transition-all",
                              selectedProvider === provider
                                ? "bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md ring-2 ring-purple-400"
                                : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
                            )}
                          >
                            {provider.charAt(0).toUpperCase() + provider.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Model Selection */}
                    {selectedProvider && aiPreferences.provider_models[selectedProvider] && (
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2 uppercase tracking-wide">
                          Model
                        </label>
                        <div className="space-y-2">
                          <button
                            onClick={async () => {
                              setSelectedModel(null)
                              setShowModelSelector(false)
                              try {
                                await aiProvidersAPI.updatePreferences({ provider: selectedProvider, model: null })
                                queryClient.invalidateQueries({ queryKey: ["aiPreferences"] })
                                toast.success('Reset to default model')
                              } catch (error) {
                                logError("Error updating AI model:", error)
                                toast.error('Failed to update model')
                              }
                            }}
                            className={clsx(
                              "w-full px-3 py-2 text-xs font-medium rounded-lg transition-all text-left",
                              !selectedModel
                                ? "bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md"
                                : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
                            )}
                          >
                            Default Model
                          </button>
                          {aiPreferences.provider_models[selectedProvider].map((model) => (
                            <button
                              key={model}
                              onClick={async () => {
                                setSelectedModel(model)
                                setShowModelSelector(false)
                                try {
                                  await aiProvidersAPI.updatePreferences({ provider: selectedProvider, model })
                                  queryClient.invalidateQueries({ queryKey: ["aiPreferences"] })
                                  toast.success(`Switched to ${model}`)
                                } catch (error) {
                                  logError("Error updating AI model:", error)
                                  toast.error('Failed to update model')
                                }
                              }}
                              className={clsx(
                                "w-full px-3 py-2.5 text-xs font-medium rounded-lg transition-all text-left flex items-center justify-between",
                                selectedModel === model
                                  ? "bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md ring-2 ring-purple-400"
                                  : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
                              )}
                            >
                              <span className="truncate">{model}</span>
                              {selectedModel === model && (
                                <CheckIconSolid className="w-4 h-4 flex-shrink-0 ml-2" />
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </>,
              document.body
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-3 sm:p-4 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
          <button
            onClick={() => navigate('/workspaces')}
            className="p-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors flex-shrink-0"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-white truncate">
              {workspace.title || 'Research Workspace'}
            </h2>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
          {/* Credits - Hidden on mobile */}
          {userTokenStats && userTokenStats.daily_tokens_total ? (
            (() => {
              const total = userTokenStats.daily_tokens_total || 0
              const remaining = userTokenStats.daily_tokens_remaining ?? 0
              const pctRemaining =
                total > 0 ? Math.max(0, Math.min(100, Math.round((remaining / total) * 100))) : 0
              return (
                <div className="hidden md:flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                  <span className="hidden lg:inline">Daily AI credits</span>
                  <div className="w-20 lg:w-24 h-2 rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
                    <div
                      className="h-2 rounded-full bg-gradient-to-r from-teal-500 to-cyan-500"
                      style={{ width: `${pctRemaining}%` }}
                    />
                  </div>
                  <span>
                    {remaining}/{total}
                  </span>
                </div>
              )
            })()
          ) : (
            <span className="hidden md:block text-xs text-slate-500 dark:text-slate-400">Loading credits…</span>
          )}

          {/* Evidence Toggle Button - Mobile Only */}
          <button
            onClick={() => setShowEvidenceDrawer(!showEvidenceDrawer)}
            className="lg:hidden p-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors relative"
            title="Toggle evidence panel"
          >
            <BookmarkIcon className="w-5 h-5" />
            {evidenceList.length > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-cyan-600 text-white text-xs rounded-full flex items-center justify-center">
                {evidenceList.length}
              </span>
            )}
          </button>

          {/* Delete Button */}
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="p-2 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors flex-shrink-0"
            title="Delete workspace"
          >
            <TrashIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Mobile Search Section */}
      <div className="lg:hidden p-4 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          Research Question
        </label>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearchConfirmation()
                }
              }}
              placeholder="Enter your research question..."
              className="w-full pl-10 pr-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm"
            />
          </div>
          <button
            onClick={handleSearchConfirmation}
            disabled={!searchQuery.trim() || isRetrieving}
            className={clsx(
              'px-4 py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 whitespace-nowrap',
              !searchQuery.trim() || isRetrieving
                ? 'bg-slate-200 dark:bg-slate-700 text-slate-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-700 hover:to-teal-700 text-white shadow-md'
            )}
          >
            {isRetrieving ? (
              <>
                <ArrowPathIcon className="w-5 h-5 animate-spin" />
                <span className="hidden sm:inline">Searching...</span>
              </>
            ) : (
              <>
                <MagnifyingGlassIcon className="w-5 h-5" />
                <span>Search</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Main Content Area - Desktop */}
      <PanelGroup direction="horizontal" className="hidden lg:flex flex-1">
        {/* Main Search and Results Panel */}
        <Panel
          defaultSize={showResultsPanel ? (evidenceExpanded ? 50 : 55) : (evidenceExpanded ? 75 : 82)}
          minSize={30}
          className="flex flex-col bg-white dark:bg-slate-800"
        >
          {/* Search Section */}
          <div className="p-4 sm:p-6 border-b border-slate-200 dark:border-slate-700">
            <div className="max-w-4xl mx-auto">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Research Question
              </label>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleSearchConfirmation()
                      }
                    }}
                    placeholder="Enter your research question or topic..."
                    className="w-full pl-10 pr-4 py-2.5 sm:py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm sm:text-base"
                  />
                </div>
                <button
                  onClick={handleSearchConfirmation}
                  disabled={!searchQuery.trim() || isRetrieving}
                  className={clsx(
                    'px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 whitespace-nowrap',
                    !searchQuery.trim() || isRetrieving
                      ? 'bg-slate-200 dark:bg-slate-700 text-slate-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-700 hover:to-teal-700 text-white shadow-md hover:shadow-lg'
                  )}
                >
                  {isRetrieving ? (
                    <>
                      <ArrowPathIcon className="w-5 h-5 animate-spin" />
                      <span className="hidden sm:inline">Searching...</span>
                    </>
                  ) : (
                    <>
                      <MagnifyingGlassIcon className="w-5 h-5" />
                      <span>Search</span>
                    </>
                  )}
                </button>
              </div>

              {/* Search Confirmation */}
              {showSearchConfirmation && (
                <div className="mt-4 p-4 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-lg">
                  <p className="text-sm text-slate-900 dark:text-white mb-3">
                    Search for papers about: <strong>"{pendingSearchQuery}"</strong>
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={confirmSearch}
                      className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-sm font-medium"
                    >
                      Confirm Search
                    </button>
                    <button
                      onClick={() => {
                        setShowSearchConfirmation(false)
                        setPendingSearchQuery('')
                      }}
                      className="px-4 py-2 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-lg text-sm font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Bulk Actions */}
              {selectedPapers.size > 0 && (
                <div className="mt-4 flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-slate-600 dark:text-slate-400">
                    {selectedPapers.size} paper{selectedPapers.size !== 1 ? 's' : ''} selected
                  </span>
                  <button
                    onClick={handleExplainSelected}
                    disabled={isGenerating}
                    className="px-3 py-1.5 text-sm bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                  >
                    Explain
                  </button>
                  {selectedPapers.size >= 2 && (
                    <button
                      onClick={handleCompareSelected}
                      disabled={isGenerating}
                      className="px-3 py-1.5 text-sm bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                    >
                      Compare
                    </button>
                  )}
                  <button
                    onClick={handleSynthesizeSelected}
                    disabled={isGenerating}
                    className="px-3 py-1.5 text-sm bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center gap-1"
                  >
                    <SparklesIcon className="w-4 h-4" />
                    Synthesize
                  </button>
                  <button
                    onClick={() => setSelectedPapers(new Set())}
                    className="px-3 py-1.5 text-sm bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-lg font-medium transition-colors"
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Results List */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="max-w-4xl mx-auto">
              {results.length === 0 ? (
                <div className="py-12 px-4">
                  <div className="text-center mb-8">
                    <BookOpenIcon className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                    <p className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                      {isReturningWorkspaceView ? 'Pick up where you left off' : (workspace?.query ? 'No search results yet' : 'Start your research')}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
                      {isReturningWorkspaceView
                        ? 'You have saved evidence and previous AI work in this workspace. Review your key papers and summaries, or run a new search to continue exploring.'
                        : workspace?.query
                          ? 'Enter a search query above to find papers, or view your saved evidence and previous syntheses in the right panel.'
                          : 'Enter a research question above and click "Search" to find relevant papers.'}
                    </p>
                  </div>

                  {isReturningWorkspaceView && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-8">
                      {/* Workspace summary card */}
                      <div className="p-5 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm flex flex-col gap-3">
                        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1 uppercase tracking-wide">
                          Workspace summary
                        </p>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
                          Research question
                        </p>
                        <p className="text-sm font-medium text-slate-900 dark:text-white">
                          {workspace.query || 'Untitled research question'}
                        </p>
                        {syntheses.length > 0 ? (
                          <div className="mt-3 space-y-3">
                            <div>
                              <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">
                                Latest AI synthesis (preview)
                              </p>
                              <div
                                className="text-xs text-slate-600 dark:text-slate-300 max-h-40 overflow-hidden prose prose-xs dark:prose-invert prose-p:my-1 prose-ul:my-1 prose-ol:my-1"
                                dangerouslySetInnerHTML={{
                                  __html: DOMPurify.sanitize(
                                    marked.parse(syntheses[0].output || '') as string
                                  ),
                                }}
                              />
                            </div>
                            <button
                              onClick={() => {
                                const latest = syntheses[0]
                                setResultViewMode('synthesis')
                                setShowResultsPanel(true)
                                setResultContent(latest.output)
                                setResultCitations(latest.citations || [])
                              }}
                              className="inline-flex items-center justify-center gap-2 px-4 py-2 text-xs font-semibold rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white transition-colors"
                            >
                              <SparklesIcon className="w-4 h-4" />
                              View full synthesis
                            </button>
                          </div>
                        ) : (
                          <div className="mt-3 space-y-2">
                            <p className="text-xs text-slate-600 dark:text-slate-400">
                              No synthesis yet. Generate one from your saved evidence to get an overview of this workspace.
                            </p>
                            {evidence.size > 0 && (
                              <button
                                onClick={handleSynthesizeSelected}
                                className="inline-flex items-center justify-center gap-2 px-4 py-2 text-xs font-semibold rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white transition-colors"
                              >
                                <SparklesIcon className="w-4 h-4" />
                                Generate synthesis from evidence
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Evidence overview card */}
                      <div className="p-5 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm">
                        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1 uppercase tracking-wide">
                          Evidence overview
                        </p>
                        <p className="text-sm text-slate-700 dark:text-slate-300 mb-3">
                          {evidenceList.length} paper{evidenceList.length !== 1 ? 's' : ''} saved as evidence
                        </p>
                        <ul className="space-y-2 max-h-40 overflow-y-auto">
                          {evidenceList.slice(0, 4).map((paper) => (
                            <li key={paper.paper_ref} className="text-xs text-slate-600 dark:text-slate-400">
                              <span className="font-medium text-slate-900 dark:text-white">
                                {paper.title}
                              </span>
                              {paper.year && (
                                <span className="text-slate-500 dark:text-slate-400"> • {paper.year}</span>
                              )}
                            </li>
                          ))}
                          {evidenceList.length > 4 && (
                            <li className="text-xs text-slate-500 dark:text-slate-400">
                              +{evidenceList.length - 4} more papers in evidence
                            </li>
                          )}
                        </ul>
                      </div>
                    </div>
                  )}

                  {evidenceList.length > 0 && (
                    <div className="mt-6 p-4 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-lg max-w-md mx-auto">
                      <p className="text-sm text-slate-700 dark:text-slate-300 mb-2">
                        <strong>{evidenceList.length} paper{evidenceList.length !== 1 ? 's' : ''}</strong> saved as evidence
                      </p>
                      <p className="text-xs text-slate-600 dark:text-slate-400">
                        View them in the Evidence panel on the right, or generate a synthesis from your saved papers.
                      </p>
                    </div>
                  )}
                  {syntheses.length > 0 && (
                    <div className="mt-4 p-4 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg max-w-md mx-auto">
                      <p className="text-sm text-slate-700 dark:text-slate-300 mb-2">
                        <strong>{syntheses.length} previous synthesis{syntheses.length !== 1 ? 'es' : ''}</strong>
                      </p>
                      <p className="text-xs text-slate-600 dark:text-slate-400">
                        View them in the Syntheses tab in the Evidence panel.
                      </p>
                    </div>
                  )}
                  {isReturningWorkspaceView && (
                    <div className="mt-8 max-w-md mx-auto text-left space-y-4">
                      <div className="p-4 rounded-xl bg-slate-800/70 dark:bg-slate-800/80 border border-slate-600 shadow-sm">
                        <p className="text-xs font-semibold text-slate-100 mb-2">
                          Suggested next steps
                        </p>
                        <ul className="list-disc list-inside text-xs text-slate-100/90 space-y-1">
                          <li>Review your saved evidence list on the right and remove papers that feel off-topic.</li>
                          <li>Generate or refine a synthesis using the <span className="font-semibold text-cyan-300">Synthesize</span> action with your evidence.</li>
                          <li>Run a new search above to fill gaps (for example, different sub-questions or time ranges).</li>
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Select All */}
                  <div className="flex items-center justify-between pb-2 border-b border-slate-200 dark:border-slate-700">
                    <button
                      onClick={handleSelectAll}
                      className="text-sm text-cyan-600 dark:text-cyan-400 hover:underline"
                    >
                      {selectedPapers.size === results.length ? 'Deselect All' : 'Select All'}
                    </button>
                    <span className="text-sm text-slate-600 dark:text-slate-400">
                      {results.length} papers
                    </span>
                  </div>

                  {/* Papers List */}
                  {results.map((paper) => {
                    const isEvidence = evidence.has(paper.paper_ref)
                    const isExpanded = expandedPapers.has(paper.paper_ref)
                    const isSelected = selectedPapers.has(paper.paper_ref)

                    return (
                      <div
                        key={paper.paper_ref}
                        className={clsx(
                          'rounded-lg border transition-all',
                          isEvidence
                            ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20'
                            : isSelected
                              ? 'border-cyan-300 dark:border-cyan-700 bg-cyan-50 dark:bg-cyan-900/10'
                              : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600'
                        )}
                      >
                        {/* Paper Header */}
                        <div className="p-4">
                          <div className="flex items-start gap-3">
                            {/* Checkbox */}
                            <button
                              onClick={() => handleToggleSelect(paper.paper_ref)}
                              className="mt-1"
                            >
                              {isSelected ? (
                                <CheckIconSolid className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                              ) : (
                                <div className="w-5 h-5 border-2 border-slate-300 dark:border-slate-600 rounded hover:border-cyan-500 transition-colors" />
                              )}
                            </button>

                            {/* Paper Info */}
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-base text-slate-900 dark:text-white mb-2">
                                {paper.title}
                              </h4>

                              {/* Authors, Year, Venue */}
                              <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                                {paper.authors.join(', ')}
                                {paper.year && ` • ${paper.year}`}
                                {paper.venue && ` • ${paper.venue}`}
                              </p>

                              {/* Fields of Study */}
                              {paper.fieldsOfStudy && paper.fieldsOfStudy.length > 0 && (
                                <div className="flex items-center gap-2 flex-wrap mb-2">
                                  {paper.fieldsOfStudy.slice(0, 3).map((field, idx) => (
                                    <span
                                      key={idx}
                                      className="px-2 py-0.5 text-xs bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 rounded"
                                    >
                                      {field}
                                    </span>
                                  ))}
                                  {paper.fieldsOfStudy.length > 3 && (
                                    <span className="text-xs text-slate-500 dark:text-slate-500">
                                      +{paper.fieldsOfStudy.length - 3} more
                                    </span>
                                  )}
                                </div>
                              )}

                              {/* Abstract or Not Available */}
                              {paper.abstract ? (
                                <p className="text-sm text-slate-500 dark:text-slate-500 line-clamp-2 mb-3">
                                  {paper.abstract}
                                </p>
                              ) : (
                                <p className="text-sm text-slate-400 dark:text-slate-500 italic mb-3">
                                  Abstract not available
                                </p>
                              )}

                              {/* Metadata Row: Citations, References, DOI, Links */}
                              <div className="flex items-center gap-4 flex-wrap mb-3">
                                {/* Citation Count */}
                                {paper.citationCount !== undefined && (
                                  <div className="group relative">
                                    <span className="text-xs text-slate-600 dark:text-slate-400 font-medium">
                                      {paper.citationCount}
                                    </span>
                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs bg-slate-900 dark:bg-slate-700 text-white rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-10">
                                      Citation Count
                                    </div>
                                  </div>
                                )}

                                {/* Reference Count */}
                                {paper.referenceCount !== undefined && (
                                  <div className="group relative">
                                    <span className="text-xs text-slate-600 dark:text-slate-400 font-medium">
                                      {paper.referenceCount}
                                    </span>
                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs bg-slate-900 dark:bg-slate-700 text-white rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-10">
                                      Reference Count
                                    </div>
                                  </div>
                                )}

                                {/* Open Access Badge */}
                                {paper.isOpenAccess && (
                                  <span className="px-2 py-0.5 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded font-medium">
                                    Open Access
                                  </span>
                                )}

                                {/* DOI Link */}
                                {paper.doi && (
                                  <a
                                    href={`http://dx.doi.org/${paper.doi.toLowerCase()}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1 text-xs text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 hover:underline"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <LinkIcon className="w-3 h-3" />
                                    DOI
                                  </a>
                                )}

                                {/* Semantic Scholar Link */}
                                {paper.url && (
                                  <a
                                    href={paper.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1 text-xs text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 hover:underline"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <AcademicCapIcon className="w-3 h-3" />
                                    Semantic Scholar
                                  </a>
                                )}

                                {/* Relevance Score */}
                                {paper.relevance_score && (
                                  <span className="text-xs text-cyan-600 dark:text-cyan-400">
                                    Relevance: {Math.round(paper.relevance_score * 100)}%
                                  </span>
                                )}
                              </div>

                              {/* Action Buttons */}
                              <div className="flex items-center gap-2 flex-wrap">
                                <button
                                  onClick={() => {
                                    if (isEvidence) {
                                      handleRemoveEvidence(paper.paper_ref)
                                    } else {
                                      handleSaveEvidence(paper.paper_ref, paper)
                                    }
                                  }}
                                  className={clsx(
                                    'flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg font-medium transition-colors',
                                    isEvidence
                                      ? 'bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/30'
                                      : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                                  )}
                                >
                                  {isEvidence ? (
                                    <>
                                      <XMarkIcon className="w-4 h-4" />
                                      Remove
                                    </>
                                  ) : (
                                    <>
                                      <BookmarkIcon className="w-4 h-4" />
                                      Save
                                    </>
                                  )}
                                </button>
                                <button
                                  onClick={() => handleExplainPaper(paper)}
                                  disabled={isGenerating}
                                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                                >
                                  <DocumentTextIcon className="w-4 h-4" />
                                  Explain
                                </button>
                                <button
                                  onClick={() => handlePaperDetail(paper)}
                                  className="px-3 py-1.5 text-sm bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-lg font-medium transition-colors"
                                >
                                  Read Full
                                </button>
                                <button
                                  onClick={() => handlePaperClick(paper)}
                                  className="px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200"
                                >
                                  {isExpanded ? (
                                    <>
                                      <ChevronDownIcon className="w-4 h-4 inline mr-1" />
                                      Less
                                    </>
                                  ) : (
                                    <>
                                      <ChevronRightIcon className="w-4 h-4 inline mr-1" />
                                      More
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Expanded View */}
                        {isExpanded && (
                          <div className="px-4 pb-4 border-t border-slate-200 dark:border-slate-700 pt-4 space-y-4">
                            {/* Full Abstract */}
                            {paper.abstract ? (
                              <div>
                                <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2">Abstract</p>
                                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                                  {paper.abstract}
                                </p>
                              </div>
                            ) : (
                              <div>
                                <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2">Abstract</p>
                                <p className="text-sm text-slate-400 dark:text-slate-500 italic">
                                  Abstract not available
                                </p>
                              </div>
                            )}

                            {/* Links Section */}
                            <div className="flex items-center gap-4 flex-wrap">
                              {paper.doi && (
                                <a
                                  href={`http://dx.doi.org/${paper.doi.toLowerCase()}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1 text-sm text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 hover:underline"
                                >
                                  <LinkIcon className="w-4 h-4" />
                                  DOI: {paper.doi}
                                </a>
                              )}
                              {paper.url && (
                                <a
                                  href={paper.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1 text-sm text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 hover:underline"
                                >
                                  <AcademicCapIcon className="w-4 h-4" />
                                  View on Semantic Scholar
                                </a>
                              )}
                            </div>

                            {/* Additional Metadata */}
                            {(paper.citationCount !== undefined || paper.referenceCount !== undefined || paper.fieldsOfStudy) && (
                              <div className="pt-3 border-t border-slate-200 dark:border-slate-700">
                                <div className="flex items-center gap-4 flex-wrap text-xs text-slate-600 dark:text-slate-400">
                                  {paper.citationCount !== undefined && (
                                    <span>
                                      <span className="font-semibold">Citations:</span> {paper.citationCount}
                                    </span>
                                  )}
                                  {paper.referenceCount !== undefined && (
                                    <span>
                                      <span className="font-semibold">References:</span> {paper.referenceCount}
                                    </span>
                                  )}
                                  {paper.fieldsOfStudy && paper.fieldsOfStudy.length > 0 && (
                                    <span>
                                      <span className="font-semibold">Fields:</span> {paper.fieldsOfStudy.join(', ')}
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                  <div ref={resultsEndRef} />
                </div>
              )}
            </div>
          </div>
        </Panel>

        <PanelResizeHandle className="w-2 bg-slate-200 dark:bg-slate-700 hover:bg-cyan-500 dark:hover:bg-cyan-600 transition-colors cursor-col-resize" />

        {/* Results Panel - Third Panel */}
        {showResultsPanel && (
          <>
            <Panel
              defaultSize={35}
              minSize={25}
              maxSize={50}
              className="flex flex-col bg-white dark:bg-slate-800 border-l border-slate-200 dark:border-slate-700"
            >
              {/* Panel Header */}
              <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-2">
                  {resultViewMode === 'explain' && <DocumentTextIcon className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />}
                  {resultViewMode === 'synthesis' && <SparklesIcon className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />}
                  {resultViewMode === 'compare' && <BookOpenIcon className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />}
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                    {resultViewMode === 'explain' && 'Explanation'}
                    {resultViewMode === 'synthesis' && 'Synthesis'}
                    {resultViewMode === 'compare' && 'Comparison'}
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowResultsPanel(false)
                    setResultViewMode(null)
                    setResultContent('')
                    setResultCitations([])
                  }}
                  className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Panel Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {isGenerating ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <ArrowPathIcon className="w-8 h-8 text-cyan-500 animate-spin mx-auto mb-4" />
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        {resultViewMode === 'explain' && 'Generating explanation...'}
                        {resultViewMode === 'synthesis' && 'Generating synthesis...'}
                        {resultViewMode === 'compare' && 'Generating comparison...'}
                      </p>
                    </div>
                  </div>
                ) : resultContent ? (
                  <div className="prose prose-sm dark:prose-invert max-w-none prose-headings:text-slate-900 dark:prose-headings:text-white prose-p:text-slate-700 dark:prose-p:text-slate-300 prose-strong:text-slate-900 dark:prose-strong:text-white prose-code:text-cyan-600 dark:prose-code:text-cyan-400 prose-pre:bg-slate-100 dark:prose-pre:bg-slate-800 prose-pre:text-slate-900 dark:prose-pre:text-slate-100 prose-a:text-cyan-600 dark:prose-a:text-cyan-400 prose-ul:text-slate-700 dark:prose-ul:text-slate-300 prose-ol:text-slate-700 dark:prose-ol:text-slate-300">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(marked.parse(resultContent || '') as string)
                      }}
                      className="text-sm leading-relaxed"
                    />
                    {resultCitations && resultCitations.length > 0 && (
                      <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
                        <h5 className="text-sm font-semibold mb-3 text-slate-900 dark:text-white">Citations</h5>
                        <ul className="list-disc list-inside space-y-2 text-sm text-slate-600 dark:text-slate-400">
                          {resultCitations.map((citation, idx) => (
                            <li key={idx}>{citation}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      No content to display
                    </p>
                  </div>
                )}
              </div>
            </Panel>
            <PanelResizeHandle className="w-2 bg-slate-200 dark:bg-slate-700 hover:bg-cyan-500 dark:hover:bg-cyan-600 transition-colors cursor-col-resize" />
          </>
        )}

        {/* Evidence Panel - Collapsible */}
        <Panel
          defaultSize={showResultsPanel ? (evidenceExpanded ? 15 : 10) : (evidenceExpanded ? 25 : 18)}
          minSize={4}
          maxSize={40}
          collapsible
          className="flex flex-col bg-white dark:bg-slate-800 border-l border-slate-200 dark:border-slate-700"
        >
          {evidenceExpanded ? (
            <>
              <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-2 flex-1">
                  <RectangleStackIcon className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                    {evidenceTab === 'evidence' ? `Evidence (${evidence.size})` : `Syntheses (${syntheses.length})`}
                  </h3>
                </div>
                <button
                  onClick={() => setEvidenceExpanded(false)}
                  className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                >
                  <ChevronRightIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-slate-200 dark:border-slate-700">
                <button
                  onClick={() => setEvidenceTab('evidence')}
                  className={clsx(
                    'flex-1 px-4 py-2 text-xs font-medium transition-colors',
                    evidenceTab === 'evidence'
                      ? 'text-cyan-600 dark:text-cyan-400 border-b-2 border-cyan-600 dark:border-cyan-400 bg-cyan-50 dark:bg-cyan-900/20'
                      : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                  )}
                >
                  Evidence ({evidence.size})
                </button>
                <button
                  onClick={() => setEvidenceTab('syntheses')}
                  className={clsx(
                    'flex-1 px-4 py-2 text-xs font-medium transition-colors',
                    evidenceTab === 'syntheses'
                      ? 'text-cyan-600 dark:text-cyan-400 border-b-2 border-cyan-600 dark:border-cyan-400 bg-cyan-50 dark:bg-cyan-900/20'
                      : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                  )}
                >
                  Syntheses ({syntheses.length})
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4">
                {evidenceTab === 'evidence' ? (
                  evidenceList.length === 0 ? (
                    <div className="text-center py-8">
                      <BookmarkIcon className="w-8 h-8 text-slate-300 dark:text-slate-600 mx-auto mb-2" />
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        No papers saved yet. Click "Save" on papers to add them.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {evidenceList.map((paper) => (
                        <div
                          key={paper.paper_ref}
                          className="p-3 rounded-lg border border-cyan-200 dark:border-cyan-800 bg-cyan-50 dark:bg-cyan-900/20"
                        >
                          <h4 className="text-xs font-medium text-slate-900 dark:text-white line-clamp-2 mb-1">
                            {paper.title}
                          </h4>
                          <p className="text-xs text-slate-600 dark:text-slate-400 mb-2">
                            {paper.authors[0]}{paper.authors.length > 1 && ' et al.'}
                            {paper.year && ` • ${paper.year}`}
                          </p>
                          <div className="flex items-center gap-3 flex-wrap">
                            <button
                              onClick={() => handleExplainPaper(paper)}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-cyan-600/10 text-cyan-600 dark:text-cyan-300 hover:bg-cyan-600/20 transition-colors"
                            >
                              <DocumentTextIcon className="w-3 h-3" />
                              Explain
                            </button>
                            <button
                              onClick={() => handlePaperDetail(paper)}
                              className="text-xs text-slate-700 dark:text-slate-300 hover:underline"
                            >
                              View details
                            </button>
                            <button
                              onClick={() => handleRemoveEvidence(paper.paper_ref)}
                              className="text-xs text-red-600 dark:text-red-400 hover:underline"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                ) : (
                  syntheses.length === 0 ? (
                    <div className="text-center py-8">
                      <SparklesIcon className="w-8 h-8 text-slate-300 dark:text-slate-600 mx-auto mb-2" />
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        No syntheses yet. Generate one using the "Synthesize" button.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {syntheses.map((synthesis) => (
                        <div
                          key={synthesis.synthesis_id}
                          className="p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer transition-colors"
                          onClick={() => {
                            setResultViewMode('synthesis')
                            setShowResultsPanel(true)
                            setResultContent(synthesis.output)
                            setResultCitations(synthesis.citations || [])
                          }}
                        >
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="flex-1">
                              <h4 className="text-xs font-semibold text-slate-900 dark:text-white mb-1">
                                Synthesis v{synthesis.version}
                              </h4>
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                {synthesis.created_at && new Date(synthesis.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <SparklesIcon className="w-4 h-4 text-cyan-600 dark:text-cyan-400 flex-shrink-0" />
                          </div>
                          <p className="text-xs text-slate-600 dark:text-slate-400 line-clamp-3">
                            {synthesis.output.substring(0, 150)}...
                          </p>
                        </div>
                      ))}
                    </div>
                  )
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full p-2">
              <button
                onClick={() => setEvidenceExpanded(true)}
                className="flex flex-col items-center gap-2 p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <RectangleStackIcon className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
                {evidence.size > 0 && (
                  <span className="text-xs font-semibold text-cyan-600 dark:text-cyan-400 bg-cyan-100 dark:bg-cyan-900/30 px-2 py-0.5 rounded-full">
                    {evidence.size}
                  </span>
                )}
              </button>
            </div>
          )}
        </Panel>
      </PanelGroup>

      {/* Mobile Results Section */}
      <div className="lg:hidden flex-1 overflow-y-auto bg-white dark:bg-slate-800">
        <div className="p-4">
          {results.length === 0 ? (
            <div className="py-12 px-4 text-center">
              <BookOpenIcon className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
              <p className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                {isReturningWorkspaceView ? 'Pick up where you left off' : (workspace?.query ? 'No search results yet' : 'Start your research')}
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
                {isReturningWorkspaceView
                  ? 'You have saved evidence and previous AI work in this workspace. Review your key papers and summaries, or run a new search to continue exploring.'
                  : workspace?.query
                    ? 'Enter a search query above to find papers, or view your saved evidence in the evidence panel.'
                    : 'Enter a research question above and click "Search" to find relevant papers.'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Select All */}
              {results.length > 0 && (
                <div className="flex items-center justify-between pb-3 border-b border-slate-200 dark:border-slate-700">
                  <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                    <input
                      type="checkbox"
                      checked={selectedPapers.size === results.length && results.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedPapers(new Set(results.map(p => p.paper_ref)))
                        } else {
                          setSelectedPapers(new Set())
                        }
                      }}
                      className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-cyan-600 focus:ring-cyan-500"
                    />
                    Select all ({results.length})
                  </label>
                </div>
              )}

              {/* Papers List - Mobile Optimized */}
              {results.map((paper) => {
                const isExpanded = expandedPapers.has(paper.paper_ref)
                const isSelected = selectedPapers.has(paper.paper_ref)
                const isEvidence = evidence.has(paper.paper_ref)

                return (
                  <div
                    key={paper.paper_ref}
                    className={clsx(
                      'rounded-xl border-2 p-4 transition-all',
                      isSelected
                        ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20'
                        : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800'
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation()
                          const newSelected = new Set(selectedPapers)
                          if (e.target.checked) {
                            newSelected.add(paper.paper_ref)
                          } else {
                            newSelected.delete(paper.paper_ref)
                          }
                          setSelectedPapers(newSelected)
                        }}
                        className="mt-1 w-5 h-5 rounded border-slate-300 dark:border-slate-600 text-cyan-600 focus:ring-cyan-500 flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-slate-900 dark:text-white mb-1 text-sm sm:text-base">
                          {paper.title}
                        </h4>
                        <p className="text-xs text-slate-600 dark:text-slate-400 mb-2">
                          {paper.authors?.slice(0, 3).join(', ')}
                          {paper.authors && paper.authors.length > 3 && ' et al.'}
                        </p>
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          {paper.year && (
                            <span className="text-xs px-2 py-0.5 bg-slate-100 dark:bg-slate-700 rounded text-slate-600 dark:text-slate-400">
                              {paper.year}
                            </span>
                          )}
                          {isEvidence && (
                            <span className="text-xs px-2 py-0.5 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 rounded font-medium">
                              Saved
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              if (isEvidence) {
                                handleRemoveEvidence(paper.paper_ref)
                              } else {
                                handleSaveEvidence(paper.paper_ref, paper)
                              }
                            }}
                            className={clsx(
                              'px-3 py-1.5 text-xs font-medium rounded-lg transition-colors',
                              isEvidence
                                ? 'bg-cyan-600 text-white'
                                : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                            )}
                          >
                            {isEvidence ? 'Saved' : 'Save'}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              setExpandedPapers(prev => {
                                const next = new Set(prev)
                                if (next.has(paper.paper_ref)) {
                                  next.delete(paper.paper_ref)
                                } else {
                                  next.add(paper.paper_ref)
                                }
                                return next
                              })
                            }}
                            className="px-3 py-1.5 text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg transition-colors"
                          >
                            {isExpanded ? 'Less' : 'More'}
                          </button>
                        </div>
                        {isExpanded && (
                          <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                            {paper.abstract ? (
                              <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                                {paper.abstract}
                              </p>
                            ) : (
                              <p className="text-xs text-slate-500 dark:text-slate-500 italic">No abstract available</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* Mobile Evidence Drawer */}
      {showEvidenceDrawer && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setShowEvidenceDrawer(false)}
          />
          <div className="fixed bottom-0 left-0 right-0 top-20 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 z-50 lg:hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                Evidence ({evidence.size})
              </h3>
              <button
                onClick={() => setShowEvidenceDrawer(false)}
                className="p-2 rounded-lg text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {evidenceList.length === 0 ? (
                <div className="text-center py-12">
                  <BookmarkIcon className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                  <p className="text-sm text-slate-500 dark:text-slate-400">No papers saved yet.</p>
                  <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Click 'Save' on papers to add them.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {evidenceList.map((paper) => (
                    <div
                      key={paper.paper_ref}
                      className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50"
                    >
                      <h4 className="font-semibold text-slate-900 dark:text-white mb-1 text-sm">
                        {paper.title}
                      </h4>
                      <p className="text-xs text-slate-600 dark:text-slate-400 mb-2">
                        {paper.authors?.slice(0, 2).join(', ')}
                        {paper.authors && paper.authors.length > 2 && ' et al.'}
                      </p>
                      <button
                        onClick={() => handleRemoveEvidence(paper.paper_ref)}
                        className="mt-2 px-3 py-1.5 text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Floating Chat Button */}
      <button
        onClick={() => setShowChat(!showChat)}
        className={clsx(
          'fixed bottom-6 right-6 w-14 h-14 rounded-full shadow-lg z-40 flex items-center justify-center transition-all',
          showChat
            ? 'bg-slate-600 dark:bg-slate-700'
            : 'bg-cyan-600 hover:bg-cyan-700'
        )}
      >
        <ChatBubbleLeftRightIcon className="w-6 h-6 text-white" />
        {!showChat && evidence.size > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {evidence.size}
          </span>
        )}
      </button>

      {/* Chat Panel - Slides in from right */}
      {showChat && (
        <div className="fixed top-16 bottom-0 right-0 w-96 bg-white dark:bg-slate-800 border-l border-slate-200 dark:border-slate-700 shadow-2xl z-50 flex flex-col">
          {/* Chat Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
            <div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Chat</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {evidence.size > 0 && `${evidence.size} papers in context`}
              </p>
            </div>
            <button
              onClick={() => setShowChat(false)}
              className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
            >
              <XCircleIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Chat Messages */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-4">
              {chatMessages.length === 0 ? (
                <div className="text-center py-8">
                  <ChatBubbleLeftRightIcon className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Ask me questions about your research or the papers.
                  </p>
                  {evidence.size > 0 && (
                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-2">
                      I'll use your {evidence.size} saved paper{evidence.size !== 1 ? 's' : ''} as context.
                    </p>
                  )}
                </div>
              ) : (
                chatMessages.map((message) => (
                  <div
                    key={message.message_id}
                    className={clsx(
                      'flex',
                      message.role === 'user' ? 'justify-end' : 'justify-start'
                    )}
                  >
                    <div
                      className={clsx(
                        'max-w-[85%] rounded-2xl px-4 py-3',
                        message.role === 'user'
                          ? 'bg-cyan-600 text-white'
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-white'
                      )}
                    >
                      <div className="text-sm leading-relaxed">
                        <div
                          className="prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1"
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(
                              marked.parse(message.content || '') as string
                            )
                          }}
                        />
                      </div>
                      {message.citations && message.citations.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-white/20 dark:border-slate-600">
                          <p className="text-xs font-semibold mb-2">Citations:</p>
                          <ul className="list-disc list-inside text-xs space-y-1">
                            {message.citations.map((citation, idx) => (
                              <li key={idx}>{citation}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
              <div ref={chatEndRef} />
            </div>
          </div>

          {/* Chat Input */}
          <div className="border-t border-slate-200 dark:border-slate-700 p-4">
            <div className="flex items-end gap-2">
              <textarea
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    handleSendChatMessage()
                  }
                }}
                placeholder="Ask a question..."
                rows={1}
                className="flex-1 resize-none px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                style={{ minHeight: '44px', maxHeight: '120px' }}
              />
              <button
                onClick={handleSendChatMessage}
                disabled={!chatInput.trim() || isSendingChat}
                className={clsx(
                  'p-3 rounded-lg transition-colors',
                  !chatInput.trim() || isSendingChat
                    ? 'bg-slate-200 dark:bg-slate-700 text-slate-400 cursor-not-allowed'
                    : 'bg-cyan-600 hover:bg-cyan-700 text-white'
                )}
              >
                {isSendingChat ? (
                  <ArrowPathIcon className="w-5 h-5 animate-spin" />
                ) : (
                  <PaperAirplaneIcon className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Paper Detail Modal */}
      <PaperDetailModal
        paper={selectedPaper ? {
          title: selectedPaper.title,
          authors: selectedPaper.authors,
          year: selectedPaper.year,
          doi: selectedPaper.doi,
          arxiv_id: selectedPaper.arxiv_id,
          abstract: selectedPaper.abstract,
          venue: selectedPaper.venue,
          url: selectedPaper.url,
          relevance_score: selectedPaper.relevance_score,
        } : null}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setSelectedPaper(null)
        }}
        onSaveToLibrary={currentDocument?.id ? async () => {
          if (selectedPaper) {
            await handleSaveEvidence(selectedPaper.paper_ref, selectedPaper)
          }
        } : undefined}
        showSaveButton={!!currentDocument?.id}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Delete Workspace"
        message="Are you sure you want to delete this workspace? This action cannot be undone. All associated data (papers, evidence, syntheses, and chat messages) will be permanently deleted."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        onConfirm={handleDeleteWorkspace}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  )
}
